import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Minimal data-fetching hook. No cache layer — this app has one user and a
 *  handful of screens, so a query library would be more moving parts than it
 *  earns. */
export function useAsync<T>(fetcher: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fetcher, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    run()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Something went wrong.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [run, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

/** Formats an instant in the pool's timezone. */
export function formatDateTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
}

/**
 * "Jul 19" — a date with no weekday and no year.
 *
 * For provenance lines, which sit in micro-type beside a measurement and have
 * to fit a 390px column. The weekday `formatDate` adds is the first thing worth
 * dropping there: nobody carrying a CYA forward cares that it was a Sunday.
 */
export function formatDay(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone,
  });
}

export function formatDate(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone,
  });
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const minutes = Math.round((Date.now() - then) / 60000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

/** `datetime-local` needs a local-time string, not an ISO instant. */
export function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * Build an absolute path inside the current vessel.
 *
 * Every screen sits under `/v/:vesselId`, so a bare `/log` would land on the
 * picker and a relative `log` would resolve against whatever depth the current
 * route happens to be at — `/v/1/history/log` from the history screen, which is
 * exactly the sort of bug that only shows up from one entry point.
 *
 * So links are built explicitly from the id in the URL. `vp("/log")` is always
 * `/v/1/log`, wherever it is called from.
 */
export function useVesselPath(): (path: string) => string {
  const { vesselId } = useParams<{ vesselId: string }>();
  return (path: string) => {
    // No vessel in the URL means this is a household-level screen — the picker,
    // or adding one. Such a screen should not be rendering vessel navigation at
    // all, and the real fix is upstream; this is the belt. Interpolating an
    // absent id produced `/v/undefined/log`, a link that looks fine, matches no
    // route, and dumps you back on the picker. Falling through to the picker
    // deliberately at least goes somewhere real.
    if (!vesselId) return "/";
    return `/v/${vesselId}${path}`;
  };
}
